import React, { useState, useEffect } from 'react';
import { useAuth } from '../App';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { 
  Gavel, 
  Plus, 
  Users, 
  Calendar, 
  FileText, 
  CheckCircle2, 
  Clock,
  Edit,
  Trash2,
  Download,
  ListChecks,
  ClipboardList
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Stati riunione
const STATI_RIUNIONE = [
  { value: 'programmata', label: 'Programmata', color: 'bg-blue-100 text-blue-700' },
  { value: 'in_corso', label: 'In Corso', color: 'bg-green-100 text-green-700' },
  { value: 'conclusa', label: 'Conclusa', color: 'bg-slate-100 text-slate-700' },
  { value: 'annullata', label: 'Annullata', color: 'bg-red-100 text-red-700' }
];

export default function ConsiglioDirettivo() {
  const { user } = useAuth();
  const [riunioni, setRiunioni] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editingRiunione, setEditingRiunione] = useState(null);
  
  const [formData, setFormData] = useState({
    titolo: '',
    data: '',
    ora: '18:00',
    luogo: '',
    ordine_del_giorno: '',
    note: ''
  });

  useEffect(() => {
    fetchRiunioni();
  }, []);

  const fetchRiunioni = async () => {
    try {
      const response = await fetch(`${API_URL}/api/governance/consiglio`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setRiunioni(data.riunioni || []);
      }
    } catch (error) {
      console.error('Error fetching riunioni:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const url = editingRiunione 
        ? `${API_URL}/api/governance/consiglio/${editingRiunione.riunione_id}`
        : `${API_URL}/api/governance/consiglio`;
      
      const response = await fetch(url, {
        method: editingRiunione ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        toast.success(editingRiunione ? 'Riunione aggiornata' : 'Riunione creata');
        setShowAddDialog(false);
        setEditingRiunione(null);
        resetForm();
        fetchRiunioni();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Errore nel salvataggio');
      }
    } catch (error) {
      toast.error('Errore di connessione');
    }
  };

  const handleDelete = async (riunioneId) => {
    if (!window.confirm('Sei sicuro di voler eliminare questa riunione?')) return;
    
    try {
      const response = await fetch(`${API_URL}/api/governance/consiglio/${riunioneId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        toast.success('Riunione eliminata');
        fetchRiunioni();
      } else {
        toast.error('Errore nell\'eliminazione');
      }
    } catch (error) {
      toast.error('Errore di connessione');
    }
  };

  const resetForm = () => {
    setFormData({
      titolo: '',
      data: '',
      ora: '18:00',
      luogo: '',
      ordine_del_giorno: '',
      note: ''
    });
  };

  const openEditDialog = (riunione) => {
    setEditingRiunione(riunione);
    setFormData({
      titolo: riunione.titolo || '',
      data: riunione.data?.split('T')[0] || '',
      ora: riunione.ora || '18:00',
      luogo: riunione.luogo || '',
      ordine_del_giorno: riunione.ordine_del_giorno || '',
      note: riunione.note || ''
    });
    setShowAddDialog(true);
  };

  const generateVerbale = async (riunioneId) => {
    toast.info('Generazione verbale in corso...');
    try {
      window.open(`${API_URL}/api/governance/consiglio/${riunioneId}/verbale`, '_blank');
      toast.success('Verbale generato!');
    } catch (error) {
      toast.error('Errore nella generazione');
    }
  };

  const generateDelibere = async (riunioneId) => {
    toast.info('Generazione foglio delibere in corso...');
    try {
      window.open(`${API_URL}/api/governance/consiglio/${riunioneId}/delibere`, '_blank');
      toast.success('Foglio delibere generato!');
    } catch (error) {
      toast.error('Errore nella generazione');
    }
  };

  // Filtra riunioni
  const riunioniInProgramma = riunioni.filter(r => 
    ['programmata', 'in_corso'].includes(r.stato)
  );
  const riunioniPassate = riunioni.filter(r => 
    ['conclusa', 'annullata'].includes(r.stato)
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="consiglio-direttivo-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Gavel className="w-7 h-7 text-indigo-600" />
            Consiglio Direttivo
          </h1>
          <p className="text-slate-600 mt-1">
            Gestisci le riunioni del consiglio direttivo e le delibere
          </p>
        </div>
        <Button onClick={() => { resetForm(); setEditingRiunione(null); setShowAddDialog(true); }}>
          <Plus className="w-4 h-4 mr-2" />
          Nuova Riunione
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Calendar className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{riunioniInProgramma.length}</p>
                <p className="text-sm text-slate-500">In Programma</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <CheckCircle2 className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">
                  {riunioni.filter(r => r.stato === 'conclusa').length}
                </p>
                <p className="text-sm text-slate-500">Concluse</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 rounded-lg">
                <ClipboardList className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">
                  {riunioni.filter(r => r.delibere_count > 0).length}
                </p>
                <p className="text-sm text-slate-500">Con Delibere</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-indigo-100 rounded-lg">
                <Gavel className="w-5 h-5 text-indigo-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{riunioni.length}</p>
                <p className="text-sm text-slate-500">Totale Anno</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="programma" className="space-y-4">
        <TabsList>
          <TabsTrigger value="programma">In Programma ({riunioniInProgramma.length})</TabsTrigger>
          <TabsTrigger value="archivio">Archivio ({riunioniPassate.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="programma">
          {riunioniInProgramma.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <Gavel className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-slate-900 mb-2">Nessuna riunione in programma</h3>
                <p className="text-slate-500 mb-4">Programma la prossima riunione del consiglio direttivo</p>
                <Button onClick={() => setShowAddDialog(true)}>
                  <Plus className="w-4 h-4 mr-2" />
                  Programma Riunione
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {riunioniInProgramma.map((riunione) => {
                const statoInfo = STATI_RIUNIONE.find(s => s.value === riunione.stato) || STATI_RIUNIONE[0];
                
                return (
                  <Card key={riunione.riunione_id} className="hover:shadow-md transition-shadow">
                    <CardHeader>
                      <div className="flex items-start justify-between">
                        <div>
                          <CardTitle className="text-lg flex items-center gap-2">
                            {riunione.titolo || 'Riunione Consiglio Direttivo'}
                            <Badge className={statoInfo.color}>{statoInfo.label}</Badge>
                          </CardTitle>
                          <CardDescription className="flex items-center gap-4 mt-2">
                            <span className="flex items-center gap-1">
                              <Calendar className="w-4 h-4" />
                              {riunione.data ? new Date(riunione.data).toLocaleDateString('it-IT', { 
                                weekday: 'long', 
                                year: 'numeric', 
                                month: 'long', 
                                day: 'numeric' 
                              }) : 'Data da definire'}
                            </span>
                            <span className="flex items-center gap-1">
                              <Clock className="w-4 h-4" />
                              {riunione.ora || '18:00'}
                            </span>
                          </CardDescription>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      {riunione.ordine_del_giorno && (
                        <div className="mb-4">
                          <h4 className="text-sm font-medium text-slate-700 flex items-center gap-1 mb-2">
                            <ListChecks className="w-4 h-4" />
                            Ordine del Giorno
                          </h4>
                          <p className="text-sm text-slate-600 whitespace-pre-line bg-slate-50 p-3 rounded-lg">
                            {riunione.ordine_del_giorno}
                          </p>
                        </div>
                      )}
                      
                      <div className="flex flex-wrap gap-2">
                        <Button variant="outline" size="sm" onClick={() => openEditDialog(riunione)}>
                          <Edit className="w-3 h-3 mr-1" />
                          Modifica
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => generateVerbale(riunione.riunione_id)}>
                          <FileText className="w-3 h-3 mr-1" />
                          Verbale
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => generateDelibere(riunione.riunione_id)}>
                          <ClipboardList className="w-3 h-3 mr-1" />
                          Delibere
                        </Button>
                        <Button variant="outline" size="sm" className="text-red-600 hover:text-red-700" onClick={() => handleDelete(riunione.riunione_id)}>
                          <Trash2 className="w-3 h-3 mr-1" />
                          Elimina
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>

        <TabsContent value="archivio">
          {riunioniPassate.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-slate-900">Archivio vuoto</h3>
                <p className="text-slate-500">Le riunioni concluse appariranno qui</p>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="pt-6">
                <div className="space-y-3">
                  {riunioniPassate.map((riunione) => {
                    const statoInfo = STATI_RIUNIONE.find(s => s.value === riunione.stato) || STATI_RIUNIONE[2];
                    
                    return (
                      <div key={riunione.riunione_id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                        <div>
                          <p className="font-medium">{riunione.titolo || 'Riunione Consiglio'}</p>
                          <p className="text-sm text-slate-500">
                            {riunione.data ? new Date(riunione.data).toLocaleDateString('it-IT') : 'N/D'}
                            {riunione.delibere_count > 0 && ` • ${riunione.delibere_count} delibere`}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button variant="outline" size="sm" onClick={() => generateVerbale(riunione.riunione_id)}>
                            <Download className="w-3 h-3 mr-1" />
                            Verbale
                          </Button>
                          <Badge className={statoInfo.color}>{statoInfo.label}</Badge>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* Add/Edit Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingRiunione ? 'Modifica Riunione' : 'Nuova Riunione'}</DialogTitle>
            <DialogDescription>
              {editingRiunione ? 'Modifica i dettagli della riunione' : 'Programma una nuova riunione del consiglio direttivo'}
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label>Titolo</Label>
              <Input 
                value={formData.titolo}
                onChange={(e) => setFormData({...formData, titolo: e.target.value})}
                placeholder="Es: Riunione straordinaria, Bilancio Q1..."
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Data *</Label>
                <Input 
                  type="date" 
                  value={formData.data}
                  onChange={(e) => setFormData({...formData, data: e.target.value})}
                  required
                />
              </div>
              <div>
                <Label>Ora</Label>
                <Input 
                  type="time" 
                  value={formData.ora}
                  onChange={(e) => setFormData({...formData, ora: e.target.value})}
                />
              </div>
            </div>

            <div>
              <Label>Luogo</Label>
              <Input 
                value={formData.luogo}
                onChange={(e) => setFormData({...formData, luogo: e.target.value})}
                placeholder="Sede dell'associazione, Online..."
              />
            </div>

            <div>
              <Label>Ordine del Giorno</Label>
              <Textarea 
                value={formData.ordine_del_giorno}
                onChange={(e) => setFormData({...formData, ordine_del_giorno: e.target.value})}
                placeholder="1. Comunicazioni del Presidente&#10;2. Approvazione verbale seduta precedente&#10;3. Varie ed eventuali"
                rows={5}
              />
            </div>

            <div>
              <Label>Note</Label>
              <Input 
                value={formData.note}
                onChange={(e) => setFormData({...formData, note: e.target.value})}
                placeholder="Note aggiuntive..."
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowAddDialog(false)}>
                Annulla
              </Button>
              <Button type="submit">
                {editingRiunione ? 'Salva Modifiche' : 'Crea Riunione'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
